import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { LatLng } from '~/_shared/types/latLng';
import {
  GroupRadiusEditLevel, ProximityType,
} from '~/_shared/types/proximity/proximity.enums';
import { latLngToFixedPrecision } from '~/_shared/utils/latLng/latLng.helpers';
import { convertMetersToUnit } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import {
  DEFAULT_PROXIMITY_BG_OPACITY, DEFAULT_PROXIMITY_BORDER_OPACITY,
} from '~/proximity/panel/form/proximityPanelForm.container';
import { createProximityRequestFromPropsWithDefaults } from '~/proximity/panel/form/proximityPanelForm.helpers';
import { stopShapeDraw } from '~/store/frontendState/mapTools/shapeDraw/shapeDraw.actionCreators';
import { useShapeDrawStateSelector } from '~/store/frontendState/mapTools/shapeDraw/shapeDraw.selectors';
import {
  ShapeDrawConfiguration, ShapeDrawShape,
} from '~/store/frontendState/mapTools/shapeDraw/shapeDraw.state';
import { useProximityToolSelector } from '~/store/frontendState/proximityTool/proximityTool.selectors';
import { addProximity } from '~/store/mapSettings/proximity/mapSettingsProximity.actionCreators';

export const useShapeDrawFinished = () => {
  const dispatch = useDispatch();
  const shapeDrawState = useShapeDrawStateSelector();
  const proximityToolSettings = useProximityToolSelector();

  const processDrawnItem = useCallback(({
    circle,
  }: {
    circle?: { center: LatLng; radiusInMeters: number };
    // more shapes can be added here as optional item so the function picks them as needed
  }) => {
    if (!shapeDrawState.isActive) {
      return;
    }

    switch (shapeDrawState.configuration) {

      case ShapeDrawConfiguration.CreateIndividualProximity:
        if (shapeDrawState.shape === ShapeDrawShape.Circle && circle) {

          let radius = convertMetersToUnit(circle.radiusInMeters, proximityToolSettings.unit);
          const decimalFactor = radius < 1 ? 1000 : radius < 10 ? 100 : 10;
          radius = Math.round(radius * decimalFactor) / decimalFactor;

          dispatch(addProximity(createProximityRequestFromPropsWithDefaults({
            type: ProximityType.DistanceRadius,
            applyTo: GroupRadiusEditLevel.IndividualLocation,
            unit: proximityToolSettings.unit,
            startingLocation: {
              address: '',
              latLng: latLngToFixedPrecision(circle.center, 5),
            },
            distance: radius,
            travelTimeMinutes: null,
            travelTimeHours: null,
            fillColor: proximityToolSettings.proximityColor.hex,
            fillOpacity: DEFAULT_PROXIMITY_BG_OPACITY,
            borderOpacity: DEFAULT_PROXIMITY_BORDER_OPACITY,
            borderColor: proximityToolSettings.borderColor.hex,
            borderWidth: proximityToolSettings.borderLineWidth,
          })));

          dispatch(stopShapeDraw());
        }
        break;

      default:
        break;
    }
  }, [dispatch, proximityToolSettings.borderColor.hex, proximityToolSettings.borderLineWidth,
    proximityToolSettings.proximityColor.hex, proximityToolSettings.unit, shapeDrawState,
  ]);

  return {
    processDrawnItem,
  };
};
